import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";

import Filters from "./Filters";
import ReportService from "../../../services/reportService";
import HeaderWrapper from "../../common/HeaderWrapper";
import ContractsByTypeList from "./ContractsByTypeList";
import { downloadCSV } from "../utils";
import { CONTRACTS_BY_TYPE_COLUMNS } from "./columns";

import { formatDate } from "../../../helpers/date";

import "./styles.scss";

const ContractsByTypePage = ({ history, codes }) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [sorted, setSorted] = useState([{ id: "createdAt", desc: false }]);

  const [filters, setFilters] = useState({
    contract_type: null,
    start_date: formatDate(
      new Date(`1/1/${new Date().getFullYear()}`),
      "yyyy-MM-dd"
    ),
    page: 0,
    pageSize: 10,
    end_date: formatDate(new Date(), "yyyy-MM-dd")
  });

  const [data, setData] = useState([]);

  const fetchData = useCallback(
    async (pageNumber, size, rtSorted) => {
      setLoading(true);
      const newSorted = rtSorted || sorted;
      let query = {
        groupByField: "customer",
        ...newSorted.reduce((acc, curr) => {
          acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
          return acc;
        }, {})
      };
      if (filters.contract_type && filters.contract_type.length) {
        query.contract_type = filters.contract_type.map(({ value }) => value);
      }
      if (filters.start_date) {
        query.start_date = filters.start_date;
      }
      if (filters.end_date) {
        query.end_date = filters.end_date;
      }
      if (filters.pageSize || size) {
        query.pageSize = size || filters.pageSize;
      }
      if (typeof filters.page === "number" || pageNumber) {
        query.page = pageNumber || filters.page;
      }
      query = queryString.stringify(query, { arrayFormat: "bracket" });
      const { data: result, pages: numPages } = await ReportService.get(
        `/contracts-by-type?${query}`
      );
      setData(result);
      setPages(numPages);
      setLoading(false);
    },
    [filters, setData, setLoading, sorted]
  );

  const handleChange = (name) => (value) => {
    setLoading(false);
    setFilters({ ...filters, [name]: value });
  };

  const handleRowClick = (rowInfo) => {
    history.push(`/app/contracts/${rowInfo.original._id}`);
  };

  const handleSortedChange = async (rtSorted) => {
    setSorted(rtSorted);
    fetchData(null, null, rtSorted);
  };

  const handlePageSizeChange = async (pageSize) => {
    setFilters({ ...filters, pageSize });
    fetchData(null, pageSize);
  };
  const handlePageChange = async (page) => {
    setFilters({ ...filters, page });
    fetchData(page, null);
  };

  const handleExport = async () => {
    setLoading(true);
    const newSorted = sorted;
    let query = {
      groupByField: "customer",
      ...newSorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {})
    };
    if (filters.contract_type && filters.contract_type.length) {
      query.contract_type = filters.contract_type.map(({ value }) => value);
    }
    if (filters.start_date) {
      query.start_date = filters.start_date;
    }
    if (filters.end_date) {
      query.end_date = filters.end_date;
    }
    query.pageSize = null;
    query.page = null;
    query = queryString.stringify(query, { arrayFormat: "bracket" });
    const { data: result } = await ReportService.get(
      `/contracts-by-type?${query}`
    );
    downloadCSV(
      `ContractsByTypeList-${formatDate(new Date(), "yyyyMMdd")}`,
      result,
      CONTRACTS_BY_TYPE_COLUMNS
    );
    setLoading(false);
  };

  return (
    <div id="customer-collection-page" className="report-page">
      <HeaderWrapper
        breadcrumbs={[{ label: "Customer Annual Revenue Analysis" }]}
      />
      <br />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);
          fetchData();
        }}
      >
        <Filters filters={filters} onChange={handleChange} codes={codes}>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading}
            onClick={() => fetchData()}
          >
            Fetch Data
          </button>
        </Filters>
      </form>
      <ContractsByTypeList
        data={data}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        sorted={sorted}
        pageSize={filters.pageSize}
        handleExport={handleExport}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

ContractsByTypePage.propTypes = {
  history: PropTypes.object,
  codes: PropTypes.array
};

export default ContractsByTypePage;
